import { supabase } from './supabaseClient';
import { getDB, getUnsynced, putItem, getAll } from './idb';

const TABLES = ['submissions', 'clients', 'templates', 'invoices'];

// In-memory per-session cache of tables that returned "does not exist" — skip them silently.
const MISSING_TABLES = new Set();
const isMissingTableError = (msg) => /relation .* does not exist|could not find the table|does not exist/i.test(msg || '');

async function pullFromCloud(userId) {
  for (const table of TABLES) {
    if (MISSING_TABLES.has(table)) continue;
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('user_id', userId);
    if (error) {
      if (isMissingTableError(error.message)) {
        MISSING_TABLES.add(table);
        continue;
      }
      console.warn('Pull error', table, error.message);
      continue;
    }
    const db = await getDB();
    const local = await db.getAll(table);
    const localMap = new Map(local.map(i => [i.id, i]));
    for (const row of data || []) {
      const localItem = localMap.get(row.id);
      if (
        !localItem ||
        new Date(row.updated_at) >= new Date(localItem.updated_at || 0)
      ) {
        await db.put(table, { ...row, synced: true });
      }
    }
  }
  // Pull profile (only overwrite if cloud is newer)
  const { data: profile } = await supabase
    .from('users_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (profile) {
    const db = await getDB();
    const localProfile = await db.get('profiles', userId);
    if (
      !localProfile ||
      localProfile.synced === true ||
      new Date(profile.updated_at || 0) >= new Date(localProfile.updated_at || 0)
    ) {
      await putItem('profiles', { ...profile, synced: true });
    }
  }
}

// Tables whose rows carry a sequential number that must never be guessed
// offline (see lib/invoiceService.js / DataContext.jsx getNextSubmissionNumber).
// A null number here means "provisional draft, not synced yet" — we call the
// server's atomic counter for the FIRST time right before the initial push,
// so the sequence has no gaps and can never collide with another record.
const NUMBER_FIELD = { submissions: 'submission_number', invoices: 'invoice_number' };
const NUMBER_RPC = { submissions: 'next_submission_number', invoices: 'next_invoice_number' };

async function assignNumberIfNeeded(table, item) {
  const field = NUMBER_FIELD[table];
  if (!field || item[field]) return item; // not a numbered table, or already has a real number
  const { data, error } = await supabase.rpc(NUMBER_RPC[table], { p_user_id: item.user_id });
  if (error || !data) return null; // couldn't reach the counter — retry on the next sync
  const numbered = { ...item, [field]: String(data) };
  // Persist the claimed number locally right away, BEFORE attempting the
  // upsert below. If the upsert then fails for an unrelated reason (dropped
  // connection, etc.), the retry reuses this same number instead of pulling
  // a fresh one from the counter and leaving a permanent gap in the sequence.
  await putItem(table, numbered);
  return numbered;
}

async function pushToCloud() {
  for (const table of TABLES) {
    if (MISSING_TABLES.has(table)) continue;
    const unsynced = await getUnsynced(table);
    for (let item of unsynced) {
      if (item._deleted) {
        const delRes = await supabase.from(table).delete().eq('id', item.id);
        if (delRes.error && isMissingTableError(delRes.error.message)) {
          MISSING_TABLES.add(table);
          break;
        }
        const db = await getDB();
        await db.delete(table, item.id);
        continue;
      }
      item = await assignNumberIfNeeded(table, item);
      if (!item) continue; // still no real number available — leave unsynced, retry later
      const { synced, _deleted, ...payload } = item;
      let { error } = await supabase.from(table).upsert(payload, { onConflict: 'id' });
      // Gracefully skip if the table doesn't exist yet (migration not applied) — and stop trying this session.
      if (error && isMissingTableError(error.message)) {
        MISSING_TABLES.add(table);
        break;
      }
      // Gracefully retry without optional columns if migration hasn't run yet.
      if (error && /column .* does not exist|could not find the .* column/i.test(error.message || '')) {
        const retry = { ...payload };
        delete retry.photos;
        delete retry.expenses;
        delete retry.last_reminded_at;
        delete retry.client_notified_at;
        delete retry.rbq_verified;
        const r2 = await supabase.from(table).upsert(retry, { onConflict: 'id' });
        error = r2.error;
      }
      if (!error) {
        await putItem(table, { ...item, synced: true });
      } else {
        console.warn('Push error', table, error.message);
      }
    }
  }
}

export async function syncAll(userId) {
  if (!userId) return { ok: false };
  if (!navigator.onLine) return { ok: false, reason: 'offline' };
  try {
    await pushToCloud();
    await pullFromCloud(userId);
    return { ok: true };
  } catch (e) {
    console.error('Sync error', e);
    return { ok: false, error: e.message };
  }
}

export async function loadLocal(userId) {
  const subs = await getAll('submissions', userId);
  const clients = await getAll('clients', userId);
  const templates = await getAll('templates', userId);
  const invoices = await getAll('invoices', userId);
  const db = await getDB();
  const profile = await db.get('profiles', userId);
  return {
    submissions: subs.filter(s => !s._deleted).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    clients: clients.filter(c => !c._deleted).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    templates: templates.filter(t => !t._deleted).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    invoices: invoices.filter(i => !i._deleted).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    profile: profile || null,
  };
}
